import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Info from "../../misc/info";
import Select from "../../inputs/select";
import Checkbox from "../../inputs/checkbox";
import CriterionSummary from "./CriterionSummary";
import { getDemographics } from "../../../services/cohort";
import "./DemographicsCard.scss";

/**
 * DemographicsCard - Criteria card for demographic targeting
 * Matches the existing Demographics component styling
 *
 * Data format: {
 *   age: [{ id: "123", title: "18-24" }, ...],
 *   gender: [{ id: "456", title: "Male" }, ...],
 *   householdIncome: [...],
 *   jobTitle: [...],
 *   childrenInHousehold: [...],
 *   lengthOfResidence: [...],
 *   homeownerOrRenter: [...],
 *   maritalStatus: [...],
 *   educationLevel: [...]
 * }
 */
function DemographicsCard({ data, onChange, isReadOnly, isCollapsed, dataCenter }) {
	const [demographicsOptions, setDemographicsOptions] = useState(null);
	const [loading, setLoading] = useState(true);
	const [showFold, setShowFold] = useState(false);

	// Determine region based on dataCenter prop
	const isUK = dataCenter === "eu-west-1";

	// Fetch demographics options on mount
	useEffect(() => {
		const fetchOptions = async () => {
			try {
				setLoading(true);
				const options = await getDemographics(isUK ? "uk" : "us");
				setDemographicsOptions(options);
			} catch (error) {
				console.error("Failed to fetch demographics options:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchOptions();
	}, [isUK]);

	// Handle Select changes
	const handleSelectChange = (fieldKey, selectedOptions) => {
		const newData = { ...data };
		newData[fieldKey] = (selectedOptions || []).map((opt) => {
			const [id, title] = opt.value.split("|");
			return { id, title };
		});
		onChange(newData);
	};

	// Handle Checkbox changes
	const handleCheckboxChange = (e, fieldKey) => {
		const [id, title] = e.target.value.split("|");
		const checked = e.target.checked;

		const newData = { ...data };
		if (!newData[fieldKey]) {
			newData[fieldKey] = [];
		}

		if (checked) {
			newData[fieldKey] = [...newData[fieldKey], { id, title }];
		} else {
			newData[fieldKey] = newData[fieldKey].filter((item) => item.id !== id);
		}

		onChange(newData);
	};

	// Convert data to select format
	const getSelectValue = (fieldKey) => {
		if (!data?.[fieldKey]) return null;
		return data[fieldKey].map((item) => ({
			value: `${item.id}|${item.title}`,
			label: item.title,
		}));
	};

	// Convert data to checkbox selected format
	const getCheckboxSelected = (fieldKey) => {
		if (!data?.[fieldKey]) return null;
		return data[fieldKey].map((item) => ({
			value: `${item.id}|${item.title}`,
			label: item.title,
		}));
	};

	// Get options for a field
	const getOptions = (fieldKey) => {
		if (!demographicsOptions?.[fieldKey]) return [];
		return demographicsOptions[fieldKey].map((el) => ({
			value: `${el.id}|${el.title}`,
			label: el.title,
		}));
	};

	// Collapsed view - show selected items as chips
	if (isCollapsed) {
		return (
			<div className="demographics-card collapsed">
				<CriterionSummary type="demographics" data={data} />
			</div>
		);
	}

	// Expanded view - matching existing Demographics component
	return (
		<div className="demographics-card expanded">
			<div className="demo-internal">
				{/* CPM Info */}
				{isUK ? (
					<Info text="There is a £1.00 CPM fee for each Experian demographic segment." />
				) : (
					<Info text="There is a $0.85 CPM fee for each Experian household income segment, and a $0.60 CPM fee for every other Experian demographic segment." />
				)}

				{/* Age - Select */}
				<Select
					label="Age"
					name="age"
					multi={true}
					selected={getSelectValue("age")}
					options={getOptions("age")}
					required={false}
					disabled={isReadOnly || loading}
					onSubmit={(arr) => handleSelectChange("age", arr)}
				/>

				{/* Gender - Checkbox row */}
				<Checkbox
					name="gender"
					options={getOptions("gender")}
					required={false}
					disabled={isReadOnly || loading}
					selected={getCheckboxSelected("gender")}
					label="Gender"
					style="row"
					onInput={(e) => handleCheckboxChange(e, "gender")}
				/>

				{/* Foldable section */}
				<div className={`demo-internal--fold ${showFold ? "show" : "hidden"}`}>
					{/* Household Income - Select */}
					<Select
						label="Household Income"
						name="householdIncome"
						multi={true}
						selected={getSelectValue("householdIncome")}
						options={getOptions("householdIncome")}
						required={false}
						disabled={isReadOnly || loading}
						onSubmit={(arr) => handleSelectChange("householdIncome", arr)}
					/>

					{/* Occupation - Select (US only) */}
					{!isUK && (
						<Select
							label="Occupation"
							name="jobTitle"
							multi={true}
							selected={getSelectValue("jobTitle")}
							options={getOptions("jobTitle")}
							required={false}
							disabled={isReadOnly || loading}
							onSubmit={(arr) => handleSelectChange("jobTitle", arr)}
						/>
					)}

					{/* Children in Household - Select */}
					<Select
						label="Child in Household"
						name="childrenInHousehold"
						multi={true}
						selected={getSelectValue("childrenInHousehold")}
						options={getOptions("childrenInHousehold")}
						required={false}
						disabled={isReadOnly || loading}
						onSubmit={(arr) => handleSelectChange("childrenInHousehold", arr)}
					/>

					{/* Length of Residence - Select (US only) */}
					{!isUK && (
						<Select
							label="Length of Residence"
							name="lengthOfResidence"
							multi={true}
							selected={getSelectValue("lengthOfResidence")}
							options={getOptions("lengthOfResidence")}
							required={false}
							disabled={isReadOnly || loading}
							onSubmit={(arr) => handleSelectChange("lengthOfResidence", arr)}
						/>
					)}

					{/* Homeowner or Renter - Checkbox row */}
					<Checkbox
						name="homeownerOrRenter"
						options={getOptions("homeownerOrRenter")}
						required={false}
						disabled={isReadOnly || loading}
						selected={getCheckboxSelected("homeownerOrRenter")}
						label="Homeowner or Renter"
						style="row"
						onInput={(e) => handleCheckboxChange(e, "homeownerOrRenter")}
					/>

					{/* Marital Status - Checkbox row */}
					<Checkbox
						name="maritalStatus"
						options={getOptions("maritalStatus")}
						required={false}
						disabled={isReadOnly || loading}
						selected={getCheckboxSelected("maritalStatus")}
						label="Marital Status"
						style="row"
						onInput={(e) => handleCheckboxChange(e, "maritalStatus")}
					/>

					{/* Education Level - Checkbox row (US only) */}
					{!isUK && (
						<Checkbox
							name="educationLevel"
							options={getOptions("educationLevel")}
							required={false}
							disabled={isReadOnly || loading}
							selected={getCheckboxSelected("educationLevel")}
							label="Education Level"
							style="row"
							onInput={(e) => handleCheckboxChange(e, "educationLevel")}
						/>
					)}
				</div>

				{/* Show More / Show Less button */}
				<button
					className="btn-simple"
					type="button"
					onClick={() => setShowFold(!showFold)}
				>
					{!showFold ? "Show More" : "Show Less"}
				</button>
			</div>
		</div>
	);
}

DemographicsCard.propTypes = {
	data: PropTypes.shape({
		age: PropTypes.array,
		gender: PropTypes.array,
		householdIncome: PropTypes.array,
		jobTitle: PropTypes.array,
		childrenInHousehold: PropTypes.array,
		lengthOfResidence: PropTypes.array,
		homeownerOrRenter: PropTypes.array,
		maritalStatus: PropTypes.array,
		educationLevel: PropTypes.array,
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	isCollapsed: PropTypes.bool,
	dataCenter: PropTypes.string,
};

DemographicsCard.defaultProps = {
	data: {},
	isReadOnly: false,
	isCollapsed: false,
	dataCenter: "us-east-1",
};

// Export helper for getting selected count (used in header)
DemographicsCard.getSelectedCount = (data) => {
	if (!data) return 0;
	const fields = [
		"age",
		"gender",
		"householdIncome",
		"jobTitle",
		"childrenInHousehold",
		"lengthOfResidence",
		"homeownerOrRenter",
		"maritalStatus",
		"educationLevel",
	];
	return fields.reduce((count, field) => count + (data[field]?.length || 0), 0);
};

export default DemographicsCard;
